import { ReactElement } from 'react';
import { formatNumber } from '../breakdownFormat';
import DerivationChain from './DerivationChain';
import TraverseTimeSeries from '../TraverseTimeSeries';

interface Props {
  detail: { work: number; power: number; rawWork: number };
  duration: number;
  ts: number[];
  riderPower: number[];
  gateStart: number;
  gateEnd: number;
  powerMeterSlope: number;
  chainEfficiency: number;
  pipelinePower: number;
}

export default function RiderSection({
  detail,
  duration,
  ts,
  riderPower,
  gateStart,
  gateEnd,
  powerMeterSlope,
  chainEfficiency,
  pipelinePower,
}: Props): ReactElement {
  const { work, power, rawWork } = detail;
  const postChainWork = rawWork * chainEfficiency;
  const pipelineWork = Number.isFinite(pipelinePower) ? pipelinePower * duration : NaN;
  const delta = Number.isFinite(pipelineWork) ? work - pipelineWork : NaN;
  const deltaPct = Number.isFinite(delta) && pipelineWork !== 0
    ? (delta / pipelineWork) * 100
    : NaN;

  return (
    <div className="cr-py-2">
      <p className="cr-text-sm cr-text-gray-500 cr-mb-4">
        Mechanical power delivered by the rider, integrated over the gate window. The 5 s buffer
        on either side is shown for context but not included in the integral.
      </p>

      <TraverseTimeSeries
        ts={ts}
        values={riderPower}
        gateStart={gateStart}
        gateEnd={gateEnd}
        yLabel="Rider Power"
        unit="W"
        title="Rider power vs time"
      />

      <div className="cr-mt-4">
        <DerivationChain
          steps={[
            { label: '∫ Rider power dt (pre-efficiency)', value: `${formatNumber(rawWork, 0)} J` },
            {
              label: '× ChainEfficiency',
              value: formatNumber(chainEfficiency, 4),
            },
            { label: '= Post-chain work', value: `${formatNumber(postChainWork, 0)} J` },
            {
              label: '× power_meter_slope',
              value: formatNumber(powerMeterSlope, 4),
            },
            { label: '= Rider work', value: `${formatNumber(work, 0)} J` },
            { label: '÷ Time', value: `${formatNumber(duration, 2)} s` },
            { label: '= Rider power', value: `${formatNumber(power, 1)} W`, isResult: true },
          ]}
        />
      </div>

      {Number.isFinite(pipelinePower) && (
        <div className="cr-mt-3 cr-pt-3 cr-border-t cr-border-gray-200 cr-text-sm">
          <div className="cr-text-gray-700 cr-font-medium cr-mb-1">Pipeline comparison</div>
          <div className="cr-grid cr-grid-cols-3 cr-gap-3 cr-font-mono cr-text-gray-700">
            <div>
              <div className="cr-text-xs cr-text-gray-500">Pipeline (Rider_PG3 × ΔT)</div>
              <div>{formatNumber(pipelineWork, 0)} J</div>
            </div>
            <div>
              <div className="cr-text-xs cr-text-gray-500">Client</div>
              <div>{formatNumber(work, 0)} J</div>
            </div>
            <div>
              <div className="cr-text-xs cr-text-gray-500">Δ (client − pipeline)</div>
              <div className={delta && Math.abs(deltaPct) > 1 ? 'cr-text-amber-700' : ''}>
                {formatNumber(delta, 0)} J
                {Number.isFinite(deltaPct) && (
                  <span className="cr-ml-2 cr-text-xs">
                    ({deltaPct >= 0 ? '+' : ''}{formatNumber(deltaPct, 2)}%)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <p className="cr-text-xs cr-text-gray-500 cr-mt-3 cr-italic">
        Transmission efficiency (<code>power_meter_slope</code>) reduces the raw integrated rider
        power before it enters the energy balance. Pipeline equivalent:{' '}
        <code>Rider_PG3 = (Rider_PG2 − ChainPower) × s</code> ≡{' '}
        <code>Rider_PG2 × ChainEff × s</code> in chain mode. <strong>Caveat:</strong> in legacy
        mode (no chain model), <code>ChainEfficiency_xG3</code> is set to{' '}
        <code>power_meter_slope</code> as a display value, and the pipeline does <em>not</em>{' '}
        multiply by it again. The client unconditionally applies both factors, so legacy-mode
        runs will show ≈3% under-estimate (×s²).
      </p>
    </div>
  );
}
